import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col, Card, CardBody, Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import moment from 'moment';
import Pagination from "react-reactstrap-pagination";
import { toast } from 'react-toastify';
import TrainingPlanDetailsModal from '../TrainingPlanDetailsModal';
import { formatTrainingPlanRequestStatus, formatAutoApplyStatus, autoApplyStatusBadgeClass } from '~/services/util';
import api from '~/services/api';
import { fetchAdminRequests, fetchRequestsByUserId, fetchRequestsByEmail } from '~/services/trainingPlanAutoApply';
import Loader from '~/components/Loader/Loader';
import { formatTrainingPlanRequestObjective, formatRequestKeepFitType } from '~/services/util'
import { debounce } from 'lodash'

const DEFAULT_STATUS = { name: 'Todos', id: 'all' }
const DEFAULT_COACH = { name: 'Todos', id: -1 }
const DEFAULT_AUTO_APPLY = { name: 'Todos', id: 'all' }

export default function TrainingPlanRequests() {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(false)
  const [trainingPlanRequestModal, setTrainingPlanRequestModal] = useState(false)
  const [tpRequestToShow, setTpRequestToShow] = useState(null)
  const [totalItems, setTotalItems] = useState(0)
  const [perPage, setPerPage] = useState(20)
  const [coachsList, setCoachsList] = useState([])
  const [showDDCoach, setShowDDCoach] = useState(false)
  const [currentCoach, setCurrentCoach] = useState(DEFAULT_COACH)
  const [showDDStatus, setShowDDStatus] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState(DEFAULT_STATUS)
  const [showDDAutoApply, setShowDDAutoApply] = useState(false)
  const [selectedAutoApply, setSelectedAutoApply] = useState(DEFAULT_AUTO_APPLY)
  const [statuses, setStatuses] = useState([])
  const [emailQuery, setEmailQuery] = useState('')

  const autoApplyFilters = [
    { name: 'Todos', id: 'all' },
    { name: 'Fila automática', id: 'auto_queue' },
  ]

  const params = new URLSearchParams(window.location.search);
  const userIdParam = params.get('userId');

  const filtersRef = useRef({
    status: DEFAULT_STATUS,
    coach: DEFAULT_COACH,
    autoApply: DEFAULT_AUTO_APPLY,
    emailQuery: '',
  })

  useEffect(() => {
    filtersRef.current = {
      status: selectedStatus,
      coach: currentCoach,
      autoApply: selectedAutoApply,
      emailQuery,
    }
  }, [selectedStatus, currentCoach, selectedAutoApply, emailQuery])

  const buildListParams = useCallback((page, status, coach, autoFilter) => {
    const listParams = {
      page,
      status: status?.id || 'all',
    };

    if (coach?.id >= 0) {
      listParams.coachId = coach.id;
    } else if (coach?.id === -2) {
      listParams.withoutCoach = true;
    }

    if (autoFilter?.id === 'auto_queue') {
      listParams.autoApplyFilter = 'auto_queue';
    }

    return listParams;
  }, []);

  const loadCoachesAndStatuses = useCallback(async () => {
    try {
      const coachsResp = await api.get(`user_coaches?role=coach-essential`)
      const allCoachs = [{ name: 'Todos', id: -1 }, { name: '<vazio>', id: -2 }, ...coachsResp.data]
      const allStatuses = [
        { name: 'Todos', id: 'all' },
        { name: 'Novos', id: 'CREATED' },
        { name: 'Abertos', id: 'OPEN' },
        { name: 'Concluídos', id: 'FINISHED' },
        { name: 'Cancelados', id: 'CANCELED' },
        { name: 'Pendentes', id: 'PENDING' }
      ]
      setStatuses(allStatuses)
      setCoachsList(allCoachs)
    } catch (e) {
      // non-blocking for list load
    }
  }, [])

  const loadTPRequests = useCallback(async (selectedPage = 1) => {
    setLoading(true)

    try {
      if (userIdParam) {
        const tpRequestsResp = await fetchRequestsByUserId(userIdParam, selectedPage)
        setRequests(tpRequestsResp?.data)
        setTotalItems(tpRequestsResp?.total)
        setPerPage(tpRequestsResp?.perPage)
        return
      }

      const { emailQuery: currentEmail, status, coach, autoApply } = filtersRef.current

      if (currentEmail) {
        const tpRequestsResp = await fetchRequestsByEmail(currentEmail)
        setRequests(tpRequestsResp?.data)
        setTotalItems(tpRequestsResp?.total)
        setPerPage(tpRequestsResp?.perPage)
        return
      }

      const tpRequestsResp = await fetchAdminRequests(
        buildListParams(selectedPage, status, coach, autoApply)
      )
      setRequests(tpRequestsResp?.data)
      setTotalItems(tpRequestsResp?.total)
      setPerPage(tpRequestsResp?.perPage)
    } catch (e) {
      toast.error('Erro ao carregar solicitações!')
    } finally {
      setLoading(false)
    }
  }, [buildListParams, userIdParam])

  useEffect(() => {
    loadCoachesAndStatuses()
    loadTPRequests(1)
  }, [loadCoachesAndStatuses, loadTPRequests])

  const searchTrainingPlanRequestsByUserEmail = useCallback(async (email) => {
    const trimmedEmail = (email || '').trim()
    setEmailQuery(trimmedEmail)
    filtersRef.current.emailQuery = trimmedEmail

    if (trimmedEmail.length > 0) {
      setLoading(true)
      try {
        const tpRequestsResp = await fetchRequestsByEmail(trimmedEmail)
        setRequests(tpRequestsResp?.data)
        setTotalItems(tpRequestsResp?.total)
        setPerPage(tpRequestsResp?.perPage)
      } catch (e) {
        toast.error('Erro ao buscar solicitações!')
      } finally {
        setLoading(false)
      }
      return
    }

    setLoading(true)
    try {
      const { status, coach, autoApply } = filtersRef.current
      const tpRequestsResp = await fetchAdminRequests(buildListParams(1, status, coach, autoApply))
      setRequests(tpRequestsResp?.data)
      setTotalItems(tpRequestsResp?.total)
      setPerPage(tpRequestsResp?.perPage)
    } catch (e) {
      toast.error('Erro ao carregar solicitações!')
    } finally {
      setLoading(false)
    }
  }, [buildListParams])

  const emailSearchRef = useRef(searchTrainingPlanRequestsByUserEmail)
  emailSearchRef.current = searchTrainingPlanRequestsByUserEmail

  const debouncedEmailSearch = useRef(
    debounce((value) => {
      emailSearchRef.current(value)
    }, 1000)
  ).current

  const reloadWithFilters = async (page, status, coach, autoFilter) => {
    setEmailQuery('')
    filtersRef.current.emailQuery = ''
    setLoading(true)
    try {
      const tpRequestsResp = await fetchAdminRequests(buildListParams(page, status, coach, autoFilter));
      setRequests(tpRequestsResp?.data)
      setTotalItems(tpRequestsResp?.total)
      setPerPage(tpRequestsResp?.perPage)
    } catch (e) {
      toast.error('Erro ao carregar solicitações!')
    } finally {
      setLoading(false)
    }
  }

  const _chooseCoach = async (coach) => {
    setCurrentCoach(coach)
    await reloadWithFilters(1, selectedStatus, coach, selectedAutoApply)
  }

  const _chooseStatus = async (status) => {
    setSelectedStatus(status)
    await reloadWithFilters(1, status, currentCoach, selectedAutoApply)
  }

  const _chooseAutoApply = async (filter) => {
    setSelectedAutoApply(filter)
    await reloadWithFilters(1, selectedStatus, currentCoach, filter)
  }

  const clearFilters = () => {
    window.location.href = `/solicitacoes-plano-treino`;
  }

  return (
    <>
      {loading && <Loader />}

      <Row>
        <Col>
          <div className="page-title-box">
            <Row>
              <Col lg={7}>
                <h4 className="page-title">Solicitações de plano de treino</h4>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <p>Solicitações ({totalItems})</p>
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', marginTop: 15, marginRight: 10, justifyContent: 'center', alignItems: 'center' }}>
                    <span style={{ marginRight: 10, fontWeight: 'bold', cursor: 'pointer' }} onClick={clearFilters}>Limpar filtros X</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', marginRight: 10 }}>
                    <span>Buscar pelo email do usuário</span>
                    <input style={{ padding: 2 }} type="text" name="search" id="search" onChange={e => debouncedEmailSearch(e.target.value)} />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Filtrar por responsável</span>
                    <Dropdown size="sm" isOpen={showDDCoach} toggle={() => setShowDDCoach(!showDDCoach)} >
                      <DropdownToggle style={{ width: 200, marginRight: 10 }}> {currentCoach?.name} </DropdownToggle>
                      <DropdownMenu
                        modifiers={{
                          setMaxHeight: {
                            enabled: true, order: 890, fn: (data) => {
                              return { ...data, styles: { ...data.styles, overflow: 'auto', maxHeight: 400, } }
                            }
                          }
                        }} >
                        {coachsList.map((elem, index) => {
                          return (
                            <DropdownItem
                              key={`co_${index}`}
                              onClick={() => _chooseCoach(elem)}>{elem.name}</DropdownItem>
                          )
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', marginRight: 10 }}>
                    <span>Filtrar por status</span>
                    <Dropdown size="sm" isOpen={showDDStatus} toggle={() => setShowDDStatus(!showDDStatus)} >
                      <DropdownToggle style={{ width: 200 }}> {selectedStatus?.name} </DropdownToggle>
                      <DropdownMenu
                        modifiers={{
                          setMaxHeight: {
                            enabled: true, order: 890, fn: (data) => {
                              return { ...data, styles: { ...data.styles, overflow: 'auto', maxHeight: 400, } }
                            }
                          }
                        }} >
                        {statuses.map((elem, index) => {
                          return (
                            <DropdownItem
                              key={`st_${index}`}
                              onClick={() => _chooseStatus(elem)}>{elem.name}</DropdownItem>
                          )
                        })}
                      </DropdownMenu>
                    </Dropdown>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>Semi-automático</span>
                    <Dropdown size="sm" isOpen={showDDAutoApply} toggle={() => setShowDDAutoApply(!showDDAutoApply)} >
                      <DropdownToggle style={{ width: 200 }}> {selectedAutoApply?.name} </DropdownToggle>
                      <DropdownMenu>
                        {autoApplyFilters.map((elem, index) => (
                          <DropdownItem key={`aa_${index}`} onClick={() => _chooseAutoApply(elem)}>{elem.name}</DropdownItem>
                        ))}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                </div>

              </div>

              <>
                <table className="table table-centered table-hover mb-0" id="btn-editable">
                  <thead>
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Usuário</th>
                      <th scope="col">Coach do Usuário</th>
                      <th scope="col">Objetivo</th>
                      <th scope="col">Data/hora solicitada</th>
                      <th scope="col">Responsável</th>
                      <th scope="col">Status</th>
                      <th scope="col">Auto aplicação</th>
                    </tr>
                  </thead>

                  <tbody>
                    {requests?.map((request, rIdx) => (
                      <tr key={`req_${rIdx}`} style={{ cursor: 'pointer' }} onClick={() => {
                        setTpRequestToShow(request)
                        setTrainingPlanRequestModal(true)
                      }}>
                        <th scope="row">
                          <span className="tabledit-span tabledit-identifier">{request.id}</span>
                        </th>

                        <td className="tabledit-view-mode"><span className="tabledit-span">
                          {request.user?.name}<br />
                          {request.user?.email}
                        </span></td>
                        <td className="tabledit-view-mode"><span className="tabledit-span">
                          {request.user?.coach?.name}
                        </span></td>

                        <td className="tabledit-view-mode"><span className="tabledit-span">{request.distance ? `${request.distance}km` : ''} {request.type === 'KEEP_FIT' ? formatRequestKeepFitType(request.trainingType) : formatTrainingPlanRequestObjective(request.distance_history)} {request.type === 'SLIMMING' || request.type === 'RETURN_INITIATION' || request.type === 'ALTERNATIVE' ? request.title : ''}</span></td>
                        <td className="tabledit-view-mode"><span className="tabledit-span">{moment(request.created_at).format('DD/MM/YYYY HH:mm:ss')}</span></td>
                        <td className="tabledit-view-mode"><span className="tabledit-span">{request.coach?.name || '--'}</span></td>
                        <td className="tabledit-view-mode"><span className={`tabledit-span badge ${request.status === 'FINISHED' ? 'badge-success' : (request.status === 'CANCELED' ? 'badge-danger' : (request.status === 'CREATED' ? 'badge-primary' : (request.status === 'PENDING' ? 'badge-warning text-dark' : 'badge-secondary')))}`}>{formatTrainingPlanRequestStatus(request.status)}</span></td>
                        <td className="tabledit-view-mode">
                          {request.auto_apply_status ? (
                            <span className={`tabledit-span badge ${autoApplyStatusBadgeClass(request.auto_apply_status)}`}>
                              {formatAutoApplyStatus(request.auto_apply_status)}
                            </span>
                          ) : (
                            <span className="tabledit-span">--</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col xs={12} style={{ overflow: 'auto' }}>
          <Pagination
            firstPageText="Início"
            lastPageText="Fim"
            previousPageText="Anterior"
            nextPageText="Próximo"
            totalItems={totalItems}
            pageSize={perPage}
            defaultActivePage={1}
            onSelect={loadTPRequests}
          />
        </Col>
      </Row>

      {trainingPlanRequestModal && <TrainingPlanDetailsModal open={trainingPlanRequestModal} toggle={() => setTrainingPlanRequestModal(false)} tpRequest={tpRequestToShow} loadTPRequests={loadTPRequests} />}
    </>

  );
}
